import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import {
  Alert,
  Card,
  Text,
  Button,
  Box,
  Flex,
  ActionIcon,
} from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  IconInfoCircle,
  IconPencil,
  IconToolsKitchen2,
} from "@tabler/icons-react";
import { useSelector } from "react-redux";

import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";
import { ErrorView, PageTitle } from "@components/shared";
import {
  ReportRecipeRunner,
  ReportRecipeEdit,
  ReportRecipeStats,
} from "@components/ReportRecipe";

export default function ReportRecipeDetail() {
  const queryClient = useQueryClient();
  const params = useParams();
  const { id } = params;
  const [searchParams] = useSearchParams();
  const userParams = {
    savedRecipeId: searchParams.get("savedReportRecipeId") || "",
  };
  const isAdmin = useSelector((state) => state.admin) ? true : false;
  const [editing, setEditing] = useState(false);

  // Invalidate query on mount to ensure fresh data
  useEffect(() => {
    queryClient.invalidateQueries([`reportRecipeDetail`, id, userParams]);
  }, [id, queryClient]);

  const {
    failureReason: error,
    isLoading,
    isFetching,
    data: info,
  } = useQuery({
    queryKey: [`reportRecipeDetail`, id, userParams],
    queryFn: async () =>
      axios
        .get(`/report-recipes/${id}/`)
        .then(({ data }) => {
          return data.response[0];
        })
        .catch((err) => {
          throw err;
        }),
    staleTime: 0, // Always consider data stale to ensure fresh status
    cacheTime: 0,
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: false, // Refetch when window regains focus
  });

  // React Query for polling report recipe execution status
  const { data: recipeSetupPolling = [] } = useQuery({
    queryKey: ["reportRecipeSetupPolling", id],
    queryFn: async () => {
      try {
        const response = await axios.post(`/report-recipes/${id}/polling/`, {
          report_recipe_id: id,
        });
        return response.data.response;
      } catch (error) {
        return [];
      }
    },
    refetchInterval: 5000, // Poll every 5 seconds
    refetchIntervalInBackground: true, // Continue polling even when tab is not active
    staleTime: 0, // Always consider data stale to ensure fresh status
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  if (error) {
    return <ErrorView text={error} />;
  }

  if (!info) return null;

  const showStats =
    info.run_count > 0 || info.average_processing_time || info.last_run_at;

  const showRunner = !isLoading && !isFetching;

  return (
    <div>
      <Flex gap="lg" align="center">
        <PageTitle
          title={info.title}
          // iconColor={entityColor.recipe}
          style={{ margin: 0 }}
          // icon={entityIcon.recipe()}
          // subtitle="Report Recipe"
        />
        {isAdmin && (
          <ActionIcon
            variant="light"
            color="gray"
            size="md"
            radius="xl"
            onClick={() => setEditing(!editing)}
          >
            <IconPencil size={16} />
          </ActionIcon>
        )}
      </Flex>
      <Box mb="lg">
        <PageTitle
          iconColor={entityColor.recipe}
          style={{ margin: 0 }}
          icon={entityIcon.recipe()}
          subtitle="Report Recipe"
        />
      </Box>
      {editing ? (
        <Card>
          <ReportRecipeEdit
            fetchData={() => {
              setEditing(false);
              queryClient.invalidateQueries([
                `reportRecipeDetail`,
                id,
                userParams,
              ]);
            }}
            id={id}
            data={info}
          />
        </Card>
      ) : (
        <>
          {info.description && (
            <Alert
              withBorder
              p="md"
              radius="md"
              mb="lg"
              title="What does this report do?"
              icon={<IconInfoCircle size={16} />}
              maw={600}
              variant="white"
              style={{
                borderColor: "var(--mantine-color-gray-3)",
              }}
            >
              <Text size="sm">{info.description}</Text>
            </Alert>
          )}
          {showStats && (
            <Box mt="md" mb="md">
              <ReportRecipeStats recipe={info} />
            </Box>
          )}
          {showRunner && (
            <ReportRecipeRunner
              info={{
                ...info,
                show_setup: false,
                last_run_at: null,
                run_count: 0,
                average_processing_time: null,
              }}
              id={id}
              setupData={recipeSetupPolling}
              showSetup={info.show_setup}
            />
          )}
        </>
      )}
    </div>
  );
}
